import React, { useState, useEffect, useRef } from "react"
import { CollapsableFilter } from "~/react/components/discover_filters/collapsable_filter"
import { Input } from "~/react/ui/input"

const t = (key: string) => window.i18n.t(key)

interface AcvFilterProps {
  value: number | null
  onChange: (value: number | null) => void
}

export function AcvFilter({ value, onChange }: AcvFilterProps) {
  const [inputValue, setInputValue] = useState<string>(value?.toString() || "")
  const inputRef = useRef<HTMLInputElement>(null)

  // Sync input value with prop changes
  useEffect(() => {
    setInputValue(value?.toString() || "")
  }, [value])

  // Autofocus when empty (only on initial mount)
  useEffect(() => {
    if (!value && inputRef.current) {
      // Small delay to ensure the filter is expanded and visible
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
      return () => clearTimeout(timeoutId)
    }
  }, []) // Empty dependency array - only run on mount

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)

    // Parse and validate the input
    const parsed = parseFloat(newValue)
    if (newValue === "" || isNaN(parsed)) {
      onChange(null)
    } else if (parsed >= 0) {
      onChange(parsed)
    }
  }

  const handleClear = () => {
    setInputValue("")
    onChange(null)
  }

  const hasValue = value !== null && value > 0

  return (
    <CollapsableFilter
      label={t("js.visitors.tam_calculator.filters.acv.label")}
      icon="dollar"
      tooltip={t("js.visitors.tam_calculator.filters.acv.tooltip")}
      hasValues={hasValue}
      onClear={handleClear}
      defaultExpanded={true}
      className="tam-calculator__acv-filter"
    >
      <div className="collapsable-filter__field">
        <label htmlFor="tam-acv-input" className="sr-only">
          {t("js.visitors.tam_calculator.filters.acv.input_label")}
        </label>
        <Input
          ref={inputRef}
          id="tam-acv-input"
          type="number"
          min="0"
          step="0.01"
          value={inputValue}
          onChange={handleChange}
          placeholder={t("js.visitors.tam_calculator.filters.acv.placeholder")}
          className="tam-calculator__acv-input"
        />
        <p className="h-hint mt-1">
          {t("js.visitors.tam_calculator.filters.acv.help_text")}
        </p>
      </div>
    </CollapsableFilter>
  )
}

